import { CustomTooltip } from '@/src/components/layout/CustomTooltip';
import { Icon } from '@/src/components/ui';
import { cn } from '@workspace/ui/lib/utils';
import { PartnerCheckmark } from '../../settings/components/PartnerBadge';
import { ReactNode, useState } from 'react';
import { MATURITY_RATINGS } from '@/src/constants/constants';
import Link from 'next/link';
import { StarRating } from '../../reviews/components/StarRating';

type ProjectTitleProps = {
  title: string;
  children?: React.ReactNode;
  className?: string;
};
export const ProjectTitle = ({
  title,
  children,
  className,
}: ProjectTitleProps) => {
  return (
    <h2
      className={`sm:text-2xl! md:text-[35px]! font-semibold! break-words flex items-center ${className}`}
    >
      {title}
      {children}
    </h2>
  );
};

//________________________________________________________________

export const ProjectDescription = ({
  description,
  isReachText,
}: {
  description: string;
  isReachText?: boolean;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (isReachText) {
    return (
      <div className="mt-6 w-full">
        <div
          className={cn(
            'prose prose-sm md:prose-base font-light transition-all duration-300 overflow-hidden relative leading-5',
            !isExpanded && 'line-clamp-4 md:line-clamp-6',
          )}
          dangerouslySetInnerHTML={{ __html: description ?? '' }}
        />

        <button
          onClick={() => setIsExpanded(!isExpanded)}
          type="button"
          className={cn(
            'text-sm font-medium text-black/70 hover:text-blue-primary transition-colors duration-200 cursor-pointer',
            'border-b border-black/70 hover:border-blue-primary',
            'mt-2 pb-0.5',
          )}
        >
          {isExpanded ? 'Hide' : 'More'}
        </button>
      </div>
    );
  }

  return (
    <p className="text-sm md:text-[15px] text-black/70 mt-3">{description}</p>
  );
};

//________________________________________________________________

type ProjectDetailsProps = {
  size: string;
  status: string;
  systems: string[] | null;
  maturityRating: string;
  placement?: 'top' | 'bottom' | 'right' | 'left';
  version: string;
  withoutFile?: boolean;
};

export const ProjectDetails = ({
  size,
  status,
  systems,
  maturityRating,
  placement,
  version,
  withoutFile = false,
}: ProjectDetailsProps) => {
  return (
    <div className="flex flex-row md:gap-9 md:h-16 h-fit md:mt-5 mt-3 font-grotesk">
      <Info label="Size" value={withoutFile ? 'TBD' : size} />
      <Divider />

      <Info
        label="Status"
        value={status}
        tip={
          <p>
            <b>Prototype</b> - Early version or under active development.
            <br />
            <b>Released</b> - Finished and available to the public.
          </p>
        }
      />
      <Divider />

      <Info
        label="System"
        value={
          withoutFile ? (
            'TBD'
          ) : (
            <span className="flex gap-3 flex-wrap">
              {systems?.map((s) => (
                <Icon
                  key={s}
                  name={s.toLowerCase()}
                  className="size-5 inline-block mr-1"
                />
              ))}
            </span>
          )
        }
      />

      <Divider />

      <Info
        label="Maturity rating"
        tip={MATURITY_RATINGS.find((r) => r.id === maturityRating)?.description}
        value={
          <Icon
            name={maturityRating as string}
            className="md:h-10 md:w-7 h-7 w-5"
          />
        }
        placement={placement}
      />
      <Divider />
      <Info label="Version" value={version} placement="right" />
    </div>
  );
};

//________________________________________________________________

type ProjectStatsProps = {
  downloads: number;
  rating: number;
  developer: string;
  devLink?: string;
  isPartner: boolean;
  type?: string;
  preview?: boolean;
};

export const ProjectStats = ({
  downloads,
  rating,
  developer,
  devLink,
  isPartner,
  type,
  preview = false,
}: ProjectStatsProps) => {
  return (
    <div className="flex justify-between md:h-11 md:mt-6 mt-3 flex-wrap">
      <StatsInfo label="Downloads" value={downloads} />
      <hr className="md:hidden border-gray-border my-1" />
      {rating > 0 ? (
        <div className="flex flex-col items-center justify-between text-sm md:text-sm font-light text-black/80">
          <div className="flex gap-2 items-center">Rating</div>
          <div className="flex gap-2 items-center">
            <span className="flex items-center text-sm font-medium">
              {rating.toFixed(1)}
            </span>
            <StarRating rating={rating} />
          </div>
        </div>
      ) : (
        <StatsInfo label="Rating" value={rating} type={type} />
      )}
      <hr className="md:hidden border-gray-border my-1" />
      <StatsInfo
        label="Developer"
        value={developer}
        isPartner={isPartner}
        devLink={devLink}
        preview={preview}
      />
    </div>
  );
};

//________________________________________________________________

function StatsInfo({
  label,
  value,
  isPartner,
  type,
  devLink,
  preview = false,
}: {
  label: string;
  value: string | number;
  isPartner?: boolean;
  type?: string;
  devLink?: string;
  preview?: boolean;
}) {
  return (
    <div className="flex flex-col items-center justify-between text-sm md:text-sm font-light text-black/80">
      <div className="flex gap-2 items-center">{label}</div>
      <span
        className={cn('text-sm font-medium flex items-center', {
          'text-xs font-light': label === 'Rating',
        })}
      >
        {value === 0 && label === 'Rating' ? (
          ' Not rated'
        ) : label === 'Developer' ? (
          !preview ? (
            <ProfLink devLink={devLink} developer={value as string} />
          ) : (
            value
          )
        ) : (
          value
        )}
        {isPartner && (
          <PartnerCheckmark className="inline-block ml-2 md:size-3 size-2" />
        )}
      </span>
    </div>
  );
}

export const ProfLink = ({
  devLink,
  developer,
}: {
  devLink?: string;
  developer: string;
}) => {
  if (devLink) {
    return (
      <Link
        href={`/${devLink}`}
        target="_blank"
        rel="noopener noreferrer"
        className="hover:text-blue-primary transition-colors duration-200 hover:cursor-pointer"
      >
        {developer}
      </Link>
    );
  }
};

function Info({
  label,
  value,
  tip,
  placement,
}: {
  label: string;
  value: ReactNode;
  tip?: ReactNode;
  placement?: 'top' | 'bottom' | 'right' | 'left';
}) {
  return (
    <div className="flex flex-col items-start justify-between md:text-base font-light text-black/80">
      <div className="md:flex md:gap-2 md:items-center md:text-base text-xs">
        {label}
        {tip && (
          <CustomTooltip label={tip} placement={placement}>
            <Icon name="question" className="size-3 md:block hidden" />
          </CustomTooltip>
        )}
      </div>
      <span className="md:text-xl text-xs font-medium">{value}</span>
    </div>
  );
}

function Divider() {
  return <hr className="border-gray-border border-r h-16 mx-1.5 md:mx-0" />;
}
